import React, { createContext, useCallback, useContext, useState, useEffect } from "react";
import { getAgentBaseUrl } from "../features/ai-assistant/conversationClient";

const ServerContext = createContext();

function normalizeEndpoint(value) {
  if (typeof value !== "string") return "";

  const trimmed = value.trim();
  if (!trimmed) return "";
  if (trimmed === "/") return trimmed;

  return trimmed.replace(/\/+$/, "");
}

const readBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return fallback;

  const normalized = value.trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(normalized)) return true;
  if (["false", "0", "no", "off"].includes(normalized)) return false;
  return fallback;
};

function resolveAiWebhookUrl(config) {
  const localModeEnabled = readBoolean(config?.localModeEnabled, false);
  const configuredWebhookUrl = normalizeEndpoint(config?.aiWebhookUrl);
  const localWebhookUrl = normalizeEndpoint(config?.localAiWebhookUrl);
  const selectedWebhookUrl =
    localModeEnabled && localWebhookUrl ? localWebhookUrl : configuredWebhookUrl;

  return selectedWebhookUrl;
}

export function ServerProvider({ children }) {
  const [servidor, setServidor] = useState("");
  const [aiWebhookUrl, setAiWebhookUrl] = useState("");
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState(false);
  const [aiAccess, setAiAccess] = useState(null);
  const [aiAccessLoading, setAiAccessLoading] = useState(false);
  const [aiAccessError, setAiAccessError] = useState("");

  const refreshAiAccess = useCallback(async (webhookUrlOverride) => {
    const webhookUrl = normalizeEndpoint(webhookUrlOverride || aiWebhookUrl);
    const username =
      typeof window !== "undefined" ? window.localStorage.getItem("username") || "" : "";

    if (!webhookUrl || !username) {
      setAiAssistantEnabled(false);
      setAiAccess(null);
      setAiAccessError("");
      return null;
    }

    setAiAccessLoading(true);
    try {
      const agentBaseUrl = getAgentBaseUrl(webhookUrl);
      const params = new URLSearchParams({ username });
      const response = await fetch(`${agentBaseUrl}/agent/v1/ai-access/effective?${params}`, {
        cache: "no-store",
      });
      if (!response.ok) {
        throw new Error(`ai-access ${response.status}`);
      }
      const payload = await response.json();
      setAiAccess(payload);
      setAiAssistantEnabled(Boolean(payload?.enabled));
      setAiAccessError("");
      return payload;
    } catch (error) {
      setAiAssistantEnabled(false);
      setAiAccess(null);
      setAiAccessError(error?.message || "No se pudo validar acceso IA");
      return null;
    } finally {
      setAiAccessLoading(false);
    }
  }, [aiWebhookUrl]);

  useEffect(() => {
    let cancelled = false;

    const loadServerFallback = async () => {
      const response = await fetch("/test.txt");
      const text = await response.text();
      return normalizeEndpoint(text);
    };

    const loadRuntimeConfig = async () => {
      try {
        const response = await fetch("/runtime-config.json", { cache: "no-store" });
        if (!response.ok) {
          throw new Error("runtime-config.json not found");
        }

        return await response.json();
      } catch {
        return null;
      }
    };

    const bootstrap = async () => {
      try {
        const config = await loadRuntimeConfig();
        const configuredServerUrl = normalizeEndpoint(config?.serverUrl);
        const serverUrl = configuredServerUrl || (await loadServerFallback());
        const resolvedAiWebhookUrl = resolveAiWebhookUrl(config);

        if (cancelled) return;

        setServidor(serverUrl);
        setAiWebhookUrl(resolvedAiWebhookUrl);
        refreshAiAccess(resolvedAiWebhookUrl);
        console.log("SERVIDOR:", serverUrl);
      } catch (error) {
        if (!cancelled) {
          console.error(error);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [refreshAiAccess]);

  return (
    <ServerContext.Provider
      value={{
        servidor,
        setServidor,
        aiWebhookUrl,
        aiAssistantEnabled,
        aiAccess,
        aiAccessLoading,
        aiAccessError,
        refreshAiAccess,
      }}
    >
      {children}
    </ServerContext.Provider>
  );
}

export function useServer() {
  return useContext(ServerContext);
}
